// ============================================================================
// BROKEN CLOCK (Throwable leftover of Alarm Clock)
// ============================================================================

import CONFIG from '../config.js';
import { distance } from '../utils/helpers.js';
import Matter from 'matter-js';

const { Body } = Matter;

export class BrokenClock {
    constructor(x, y, width, height, damage, physics = null) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.damage = damage;
        this.physics = physics;
        this.isPickable = true;
        this.isCarried = false;
        this.carriedBy = null;
        this.rotation = Math.random() * Math.PI * 2;
        this.impactForce = 0;

        if (physics) {
            const { CATEGORY, MASK } = physics.constructor;
            this.body = physics.createRectangle(x, y, width, height, {
                isStatic: false,
                frictionAir: CONFIG.throwableFrictionAir,  // Air friction (affects both linear and angular velocity)
                friction: 0.7,  // Increased surface friction for more realistic sliding
                restitution: CONFIG.wallBounceEnergyLoss,
                mass: 1,
                collisionFilter: {
                    category: CATEGORY.THROWABLE,
                    mask: MASK.THROWABLE
                }
            });
            
            // Random initial pop-off velocity (use spawn speed multiplier)
            const angle = Math.random() * Math.PI * 2;
            const baseSpeed = 100 + Math.random() * 100;
            const speed = baseSpeed * CONFIG.throwableSpawnSpeedMultiplier;
            physics.setVelocity(this.body, Math.cos(angle) * speed, Math.sin(angle) * speed);
            
            // Random rotation speed (between min and max, not too fast)
            const rotationSpeed = CONFIG.throwableRotationSpeedMin + 
                Math.random() * (CONFIG.throwableRotationSpeedMax - CONFIG.throwableRotationSpeedMin);
            const rotationDirection = Math.random() < 0.5 ? -1 : 1;
            const finalRotationSpeed = rotationSpeed * rotationDirection;
            Body.setAngularVelocity(this.body, finalRotationSpeed);
            
            physics.linkEntity(this, this.body);
            
            // Create sensor body for pickup detection
            const sensorRadius = (CONFIG.pickupRadius || 40) * 0.8; // 20% smaller
            this.sensorBody = physics.createSensor(x, y, sensorRadius);
            physics.linkEntity(this, this.sensorBody);
        } else {
            this.sensorBody = null;
        }
    }
    
    update(dt, tilemap) {
        if (this.isCarried) return;
        
        if (this.body) {
            // Apply manual angular damping (Matter.js doesn't have built-in angularDamping)
            // angularDamping is the fraction remaining after 1 second, so we apply it per frame
            if (this.body.angularVelocity !== 0) {
                const angularDamping = CONFIG.throwableAngularDamping;
                Body.setAngularVelocity(this.body, this.body.angularVelocity * Math.pow(angularDamping, dt));
            }
            
            // Sync position from physics body
            this.x = this.body.position.x;
            this.y = this.body.position.y;
            this.rotation = this.body.angle;
            
            // Update sensor body position
            if (this.sensorBody && this.physics) {
                this.physics.setPosition(this.sensorBody, this.x, this.y);
            }
        }
    }

    isNearPlayer(player) {
        if (this.isCarried || !player || !player.body || !this.sensorBody || !this.physics) return false;
        // Use physics sensor to detect if player is in range
        return this.physics.isPlayerInSensor(this.sensorBody, player.body);
    }

    pickup(player) {
        if (this.isCarried) return false;
        this.isCarried = true;
        this.carriedBy = player;
        if (this.body && this.physics) {
            this.physics.setVelocity(this.body, 0, 0);
            Body.setAngularVelocity(this.body, 0);
            if (player.body) {
                this.physics.attachToPlayer(player.body, this.body, 0, -CONFIG.playerSize - 10);
            }
        }
        return true;
    }

    drop() {
        if (!this.isCarried) return;
        this.isCarried = false;
        if (this.body && this.physics) {
            this.physics.detachFromPlayer(this.body);
        }
        this.carriedBy = null;
    }

    throwAt(targetX, targetY, throwForce) {
        if (!this.isCarried) return;
        this.impactForce = throwForce;
        if (this.body && this.physics) {
            this.physics.detachFromPlayer(this.body);
        }
        this.isCarried = false;
        this.carriedBy = null;
        
        const dx = targetX - this.x;
        const dy = targetY - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist > 0 && this.body && this.physics) {
            // Use configurable throw speed multiplier
            const adjustedForce = throwForce * CONFIG.throwableThrowSpeedMultiplier;
            const vx = (dx / dist) * adjustedForce;
            const vy = (dy / dist) * adjustedForce;
            this.physics.setVelocity(this.body, vx, vy);
            
            // Random rotation speed (between min and max, not too fast)
            const rotationSpeed = CONFIG.throwableRotationSpeedMin + 
                Math.random() * (CONFIG.throwableRotationSpeedMax - CONFIG.throwableRotationSpeedMin);
            const rotationDirection = Math.random() < 0.5 ? -1 : 1;
            const finalRotationSpeed = rotationSpeed * rotationDirection;
            Body.setAngularVelocity(this.body, finalRotationSpeed);
        }
    }

    updateCarriedPosition(playerX, playerY) {
        if (this.isCarried && !this.body) {
            this.x = playerX;
            this.y = playerY - CONFIG.playerSize - 5;
        }
    }

    update(dt) {
        if (this.isCarried) return;
        
        if (this.body) {
            this.x = this.body.position.x;
            this.y = this.body.position.y;
            this.rotation = this.body.angle;
            
            // Update sensor body position
            if (this.sensorBody && this.physics) {
                this.physics.setPosition(this.sensorBody, this.x, this.y);
            }
        }
    }

    draw(ctx, camera) {
        ctx.save();
        
        // Use physics body position if available (works even when carried)
        let drawX = this.x;
        let drawY = this.y;
        let drawRotation = this.rotation;
        
        if (this.body) {
            drawX = this.body.position.x;
            drawY = this.body.position.y;
            drawRotation = this.body.angle;
        }
        
        ctx.translate(drawX, drawY);
        ctx.rotate(drawRotation);

        let clockColor = '#8B6914'; // Default broken color
        if (this.damage >= CONFIG.alarmClockDamageThresholds?.[2]) clockColor = '#4B2010';
        else if (this.damage >= CONFIG.alarmClockDamageThresholds?.[1]) clockColor = '#6B3410';
        else if (this.damage >= CONFIG.alarmClockDamageThresholds?.[0]) clockColor = '#8B4010';

        ctx.fillStyle = clockColor;
        ctx.fillRect(-this.width / 2, -this.height / 2, this.width, this.height);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.strokeRect(-this.width / 2, -this.height / 2, this.width, this.height);

        // Broken interior look
        ctx.fillStyle = '#8B6914';
        ctx.fillRect(-this.width / 2 + 1, -this.height / 2 + 1, this.width - 2, this.height - 2);
        ctx.fillStyle = '#000';
        ctx.beginPath();
        ctx.arc(0, 0, 2, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    }
}
